'use client';

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  Button,
  ExpandableCallout,
  ScrollableWeekStrip,
  SingleSelect,
  Spinner,
  TextButton,
} from '@galapagos/design-system/web';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import type { CareBookingPurpose } from '@galapagos/shared';
import { currentBookingIncentive } from '@/services/link-incentive';
import { useSignupApi } from '../../hooks/useSignupApi';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import { useStepHeading } from '../../hooks/useStepHeading';
import { wrapUpExitStep } from '../../wrap-up/branching';
import { useWrapUpContext } from '../../wrap-up/useWrapUpContext';
import { classifyBookingFailure } from '../../wrap-up/booking-failure';
import {
  bookLabelParts,
  groupSlotsByDay,
  memberTimeZone,
  zoneLabel,
} from '../../wrap-up/booking-slots';
import { PrivateFields } from '../../components/PrivateFields';
import { gated } from './gated';
import { IncentiveReminder } from './IncentiveReminder';
import { WrapUpActions, WrapUpFrame } from './WrapUpFrame';

type BookingProblem = 'slot-taken' | 'generic' | null;

const subscribeToNothing = () => () => {};

/**
 * `/booking/`: pick a day and a time for the first call, shown in the
 * member's zone, and book it. A taken time re-reads the list; a member who
 * already has a call moves on; an empty list goes to the no-times screen.
 * With a purpose it books that call instead (the HDHP cost call with Member
 * Support), under its own copy and without the first-call incentive.
 */
function BookingStep({ purpose }: { purpose?: CareBookingPurpose }) {
  const { t, i18n } = useTranslation('signup');
  const language = i18n.language;
  const costCall = purpose === 'hdhp';
  const title = costCall ? t('wrapUp.hdhpBooking.title') : t('wrapUp.booking.title');
  const headingRef = useStepHeading(title);
  const api = useSignupApi();
  const searchParams = useSearchParams();
  const scenario = searchParams.get('scenario');
  const search = searchParams.toString();
  // The campaign window is judged on the visitor's clock, so only in the browser.
  const inBrowser = useSyncExternalStore(
    subscribeToNothing,
    () => true,
    () => false,
  );
  const incentive = useMemo(
    () => (inBrowser && !purpose ? currentBookingIncentive(search) : null),
    [inBrowser, purpose, search],
  );
  const [incentiveClosed, setIncentiveClosed] = useState(false);
  const { goToStep } = useSignupNavigation();
  const wrapUp = useWrapUpContext();
  // A minor books with a parent present and ends on "registered" (production parity).
  const minor = wrapUp.ready && wrapUp.context.minor;
  const exitStep = wrapUpExitStep(wrapUp.ready ? wrapUp.context : undefined);
  const zone = useMemo(() => memberTimeZone(), []);
  const slots = useQuery({
    queryKey: ['signup', 'booking-slots', scenario, purpose ?? null],
    queryFn: () => api.getBookingSlots(purpose ? { purpose } : undefined),
    retry: false,
  });
  const days = useMemo(
    () => (slots.data ? groupSlotsByDay(slots.data.slots, zone, language) : []),
    [slots.data, zone, language],
  );
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [problem, setProblem] = useState<BookingProblem>(null);
  const selectedDay = days.find((day) => day.key === dayKey) ?? days[0];
  const noTimes = slots.isSuccess && days.length === 0;

  const left = useRef(false);
  useEffect(() => {
    if (!noTimes || !wrapUp.ready || left.current) return;
    left.current = true;
    // A minor has no "we'll reach out" step: production ends their flow here.
    goToStep(wrapUp.context.minor ? 'registered' : 'booking-unavailable');
  }, [noTimes, wrapUp, goToStep]);

  const inFlight = useRef(false);
  const book = useMutation({
    mutationFn: (slot: string) =>
      api.bookAppointment({
        startsAt: slot,
        source: 'member-sheet',
        ...(purpose ? { purpose } : {}),
      }),
    onSuccess: () => goToStep(exitStep),
    onError: (error) => {
      inFlight.current = false;
      const failure = classifyBookingFailure(error);
      if (failure === 'slot-taken') {
        setStartsAt(null);
        setProblem('slot-taken');
        void slots.refetch();
      } else {
        setProblem('generic');
      }
    },
  });
  const onBook = () => {
    // The exit depends on minor status, so nothing books before it is known.
    if (startsAt === null || inFlight.current || !wrapUp.ready) return;
    inFlight.current = true;
    setProblem(null);
    book.mutate(startsAt);
  };

  let status = '';
  if (slots.isPending) status = t('wrapUp.booking.loading');
  else if (slots.isError) status = t('wrapUp.booking.loadError');
  else if (book.isPending) status = t('wrapUp.booking.booking');
  else if (problem === 'slot-taken') status = t('wrapUp.booking.slotTaken');
  else if (problem === 'generic') status = t('wrapUp.booking.genericError');
  const statusTone = slots.isError || problem !== null ? 'error' : 'neutral';

  const bookLabel =
    startsAt === null
      ? t('wrapUp.booking.bookCta')
      : t('wrapUp.booking.bookCtaSelected', bookLabelParts(startsAt, zone, language));
  let bookState: 'Default' | 'Loading' | 'Disabled' = 'Default';
  if (book.isPending || !wrapUp.ready) bookState = 'Loading';
  else if (startsAt === null) bookState = 'Disabled';

  return (
    <WrapUpFrame
      title={title}
      description={costCall ? t('wrapUp.hdhpBooking.description') : t('wrapUp.booking.description')}
      headingRef={headingRef}
    >
      {/* Remounts open once the context says minor: the parent requirement is
          not something to hide behind a tap. */}
      <ExpandableCallout
        key={minor ? 'minor' : 'adult'}
        title={costCall ? t('wrapUp.hdhpBooking.aboutTitle') : t('wrapUp.booking.aboutTitle')}
        defaultExpanded={minor}
        actionId="signup.booking-about.toggle"
        data-testid="signup-booking-about-callout"
      >
        <p className="signup-step__text">
          {costCall
            ? t('wrapUp.hdhpBooking.aboutBody')
            : minor
              ? t('wrapUp.booking.minorAboutBody')
              : t('wrapUp.booking.aboutBody')}
        </p>
      </ExpandableCallout>
      {slots.isPending ? <Spinner accessibilityLabel={t('wrapUp.booking.loading')} /> : null}
      {selectedDay ? (
        // The day and time a member books are care information: no
        // autocapture or RUM click from these choices.
        <PrivateFields>
          <ScrollableWeekStrip
            weeks={[
              days.map((day) => ({
                date: day.key,
                dayLabel: day.dayLabel,
                weekdayLabel: day.weekdayLabel,
                // The submitted slot stays on screen until the booking answers.
                selectable: !book.isPending,
              })),
            ]}
            selectedDate={selectedDay.key}
            onSelectDate={(date) => {
              if (inFlight.current) return;
              setDayKey(date);
              setStartsAt(null);
              setProblem(null);
            }}
            accessibilityLabel={t('wrapUp.booking.dayGroupLabel')}
            variant="picker"
            actionId="signup.booking-day.select"
            data-testid="signup-booking-days"
          />
          <SingleSelect
            label={t('wrapUp.booking.timeGroupLabel')}
            hideLabel
            actionId="signup.booking-time.change"
            data-testid="signup-booking-times"
            options={selectedDay.times.map((time) => ({
              value: time.startsAt,
              label: time.label,
              disabled: book.isPending,
            }))}
            value={startsAt}
            onSelect={(value) => {
              if (inFlight.current) return;
              setStartsAt(value);
              setProblem(null);
            }}
          />
          <p className="signup-step__note">
            {t('wrapUp.booking.zoneLine', {
              zone: zoneLabel(zone, selectedDay.times[0]?.startsAt ?? '', language),
            })}
          </p>
        </PrivateFields>
      ) : null}
      {incentive !== null && !incentiveClosed ? (
        <IncentiveReminder
          incentive={incentive}
          onClose={() => {
            setIncentiveClosed(true);
            headingRef.current?.focus();
          }}
        />
      ) : null}
      <WrapUpActions status={status} statusTone={statusTone}>
        {slots.isError ? (
          <TextButton
            label={t('wrapUp.booking.retry')}
            actionId="signup.booking-retry.activate"
            data-testid="signup-booking-retry-button"
            onPress={() => void slots.refetch()}
          />
        ) : (
          // The label names the chosen time, so the button sits in the same
          // private subtree as the choices. The wrapper hides it from the
          // action bar's own stretch, so it asks for it here.
          <PrivateFields>
            <Button
              size="L"
              type="Primary"
              width="stretch"
              label={bookLabel}
              actionId="signup.booking-book.activate"
              data-testid="signup-booking-book-button"
              state={bookState}
              onPress={onBook}
            />
          </PrivateFields>
        )}
      </WrapUpActions>
    </WrapUpFrame>
  );
}

export const BookingScreen = gated(BookingStep, 'booking');

function HdhpBookingStep() {
  return <BookingStep purpose="hdhp" />;
}

/** `/hdhp-booking/`: the free cost call the HDHP notice offers. */
export const HdhpBookingScreen = gated(HdhpBookingStep, 'hdhp-booking');
